// PAN  : 5 letters + 4 digits + 1 letter        e.g. AABCS1429K
// IFSC : 4 letters + 0 + 6 alphanumeric         e.g. HDFC0001234
export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
export const PIN_RE = /^[1-9][0-9]{5}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const UPPER_KEYS = ["pan", "gstin", "ifsc"];
export const REQUIRED_KEYS = ["company_name", "pan", "account_number", "ifsc"];

export const FORMAT_HINTS = {
  pan: "Expected format: AAAAA9999A (5 letters, 4 digits, 1 letter)",
  ifsc: "Expected format: AAAA0XXXXXX (4 letters, 0, then 6 alphanumeric)",
  gstin: "Expected 15 characters: 99AAAAA9999A9Z9",
  pin: "Expected 6 digits",
  email: "Expected a valid email address",
  mobile: "Expected a 10-digit Indian mobile number starting 6-9",
};

// normalise a value as the user types
export function normalizeValue(key, raw) {
  let v = raw ?? "";
  if (UPPER_KEYS.includes(key)) {
    v = v.replace(/\s+/g, "").toUpperCase();
  }
  if (key === "pin") v = v.replace(/[^0-9]/g, "").slice(0, 8);
  return v;
}

/**
 * status levels:
 *  "error"   -> hard validation failure (PAN / IFSC) => row is flagged invalid
 *  "warn"    -> soft format issue (GSTIN / PIN / email / mobile)
 *  "needs"   -> commonly-required field left blank
 *  "ok"      -> fine
 */
export function cellStatus(key, value) {
  const v = (value ?? "").toString().trim();
  if (!v) {
    return REQUIRED_KEYS.includes(key)
      ? { level: "needs", message: "Missing - not found in the source, please fill it in" }
      : { level: "ok" };
  }
  if (key === "pan" && !PAN_RE.test(v)) return { level: "error", message: `Invalid PAN. ${FORMAT_HINTS.pan}` };
  if (key === "ifsc" && !IFSC_RE.test(v)) return { level: "error", message: `Invalid IFSC. ${FORMAT_HINTS.ifsc}` };
  if (key === "gstin" && !GSTIN_RE.test(v)) return { level: "warn", message: `GSTIN looks off. ${FORMAT_HINTS.gstin}` };
  if (key === "pin" && !PIN_RE.test(v)) return { level: "warn", message: `PIN looks off. ${FORMAT_HINTS.pin}` };
  if (key === "email" && !EMAIL_RE.test(v)) return { level: "warn", message: `Email looks off. ${FORMAT_HINTS.email}` };
  if (key === "mobile") {
    const digits = v.replace(/\D/g, "");
    const last10 = digits.slice(-10);
    if (digits.length < 10 || !/^[6-9][0-9]{9}$/.test(last10)) {
      return { level: "warn", message: `Mobile looks off. ${FORMAT_HINTS.mobile}` };
    }
  }
  return { level: "ok" };
}

export function rowIssues(row) {
  const errors = [];
  const warnings = [];
  const missing = [];
  Object.keys(row || {}).forEach(() => {});
  ["pan", "ifsc", "gstin", "pin", "email", "mobile", ...REQUIRED_KEYS].forEach((k) => {
    const s = cellStatus(k, row?.[k]);
    if (s.level === "error" && !errors.includes(k)) errors.push(k);
    if (s.level === "warn" && !warnings.includes(k)) warnings.push(k);
    if (s.level === "needs" && !missing.includes(k)) missing.push(k);
  });
  return { errors, warnings, missing, invalid: errors.length > 0 };
}

export function summarize(rows) {
  let invalid = 0;
  let warnings = 0;
  let incomplete = 0;
  rows.forEach((r) => {
    const i = rowIssues(r);
    if (i.invalid) invalid += 1;
    if (i.warnings.length) warnings += 1;
    if (i.missing.length) incomplete += 1;
  });
  return { total: rows.length, invalid, valid: rows.length - invalid, warnings, incomplete };
}
